// T2.16b-EXTEND + T2.14b: BwaTrendTile mit JA-Anker + Severity-Trio.
//
// Daten aus stb-Pipeline (POST /api/v1/ingest/stb-pdf -> bwa-Tabelle).
// Quelle ist Pahl-Diederichsen-Mail-Daemon, parser in app/stb/service.py.

import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { BwaItem, BwaTrendResponse, fetchBwaTrend } from "../lib/api";

interface Props {
  firmaId: string;
}

interface ChartRow {
  monat_label: string;
  ebit_marge_pct: number | null;
  ebit_marge_pct_ytd: number | null;
  personalquote_pct: number | null;
  umsatz_eur: number | null;
}

const MONAT_KURZ = [
  "Jan",
  "Feb",
  "Mär",
  "Apr",
  "Mai",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Okt",
  "Nov",
  "Dez",
];

function _toRow(item: BwaItem): ChartRow {
  const umsatz = item.umsatz_eur ? parseFloat(item.umsatz_eur) : null;
  const aufw = item.aufwendungen_eur ? parseFloat(item.aufwendungen_eur) : null;
  // Personalquote als grobe Schaetzung — ohne separates Personalkosten-Feld
  // approximieren wir aus Aufwendungen (Folge: Backend BWA-Schema erweitern
  // um personalkosten_eur aus DATEV-Schluessel-Mapping).
  const personalquote =
    umsatz && aufw && umsatz > 0
      ? Math.round((aufw / umsatz) * 100 * 10) / 10
      : null;
  const ytd = item.ebit_marge_pct_ytd ? parseFloat(item.ebit_marge_pct_ytd) : null;
  return {
    monat_label: `${MONAT_KURZ[item.monat - 1] ?? item.monat}/${String(item.jahr).slice(2)}`,
    ebit_marge_pct: item.ebit_marge_pct,
    ebit_marge_pct_ytd: ytd,
    personalquote_pct: personalquote,
    umsatz_eur: umsatz,
  };
}

// T21 Severity-Trio: aktueller EBIT-Marge -> cyan/amber/pink
function severityClass(margePct: number | null): string {
  if (margePct == null) return "border-slate-700/60";
  if (margePct < 0) return "border-pink-500/40";
  if (margePct < 5) return "border-amber-500/40";
  return "border-cyan-500/40";
}

export default function BwaTrendTile({ firmaId }: Props) {
  const query = useQuery<BwaTrendResponse, Error>({
    queryKey: ["bwa-trend", firmaId],
    queryFn: () => fetchBwaTrend(firmaId, 12),
    staleTime: 10 * 60 * 1000,
  });

  const data = useMemo(() => {
    const items = query.data?.items ?? [];
    return [...items].reverse().map(_toRow);
  }, [query.data]);

  if (query.isLoading) {
    return (
      <section className="rounded-2xl border border-slate-700/60 bg-slate-950 p-4 sm:p-5">
        <h2 className="text-[11px] font-semibold uppercase tracking-widest text-slate-100">
          BWA-Trend
        </h2>
        <p className="text-xs text-slate-400 mt-2">Lade …</p>
      </section>
    );
  }

  const items = query.data?.items ?? [];
  if (items.length === 0) {
    return (
      <section className="rounded-2xl border border-slate-700/60 bg-slate-950 p-4 sm:p-5">
        <h2 className="text-[11px] font-semibold uppercase tracking-widest text-slate-100">
          BWA-Trend
        </h2>
        <p className="text-xs text-slate-400 mt-2">
          Wartet auf BWA-Mail. Briefing-Daemon zieht 06:30, postet via
          /api/v1/ingest/stb-pdf in die bwa-Tabelle.
        </p>
      </section>
    );
  }

  const latest = items[0];
  const jaAnker = query.data?.ja_anker ?? null;
  // T5: Hero-Pill zeigt YTD-Marge statt isoliertem Monat — ehrlicher
  // (Marge-Schwankung pro Monat schwankt extrem, YTD glaettet).
  const latestYtd = latest.ebit_marge_pct_ytd ? parseFloat(latest.ebit_marge_pct_ytd) : null;
  const sevClass = severityClass(latestYtd);

  return (
    <section
      className={`rounded-2xl border ${sevClass} bg-slate-950 p-4 sm:p-5 space-y-3`}
    >
      <header className="flex items-baseline justify-between gap-2">
        <h2 className="text-[11px] font-semibold uppercase tracking-widest text-slate-100">
          BWA-Trend
          <span className="ml-2 text-slate-500 font-normal text-[10px]">
            12 Monate · letzte: {MONAT_KURZ[latest.monat - 1]} {latest.jahr}
          </span>
        </h2>
        {latestYtd != null && (
          <span
            className={`text-xs tabular-nums ${
              latestYtd < 0
                ? "text-pink-200"
                : latestYtd < 5
                  ? "text-amber-200"
                  : "text-cyan-200"
            }`}
          >
            EBIT-Marge YTD {latestYtd.toFixed(2)}%
          </span>
        )}
      </header>

      <div className="h-44 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke="#334155" opacity={0.3} />
            <XAxis dataKey="monat_label" tick={{ fontSize: 10, fill: "#94a3b8" }} />
            <YAxis
              yAxisId="pct"
              tick={{ fontSize: 10, fill: "#94a3b8" }}
              tickFormatter={(v) => `${v}%`}
            />
            <Tooltip
              contentStyle={{
                background: "#020617",
                border: "1px solid #334155",
                borderRadius: "0.5rem",
                fontSize: "11px",
              }}
              formatter={(v: number, name: string) =>
                v == null ? ["—", name] : [`${v.toFixed(1)}%`, name]
              }
            />
            {jaAnker && (
              <ReferenceLine
                yAxisId="pct"
                y={jaAnker.ebit_marge_pct}
                stroke="#22d3ee"
                strokeDasharray="4 4"
                label={{
                  value: `JA ${jaAnker.jahr}: ${jaAnker.ebit_marge_pct.toFixed(1)}%`,
                  position: "right",
                  fill: "#67e8f9",
                  fontSize: 9,
                }}
              />
            )}
            <Bar
              yAxisId="pct"
              dataKey="personalquote_pct"
              fill="#7c3aed"
              fillOpacity={0.6}
              name="Aufwandsquote"
            />
            <Line
              yAxisId="pct"
              dataKey="ebit_marge_pct"
              stroke="#10b981"
              strokeWidth={2}
              dot={{ r: 3 }}
              name="EBIT-Marge"
              connectNulls
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      <p className="text-[10px] text-slate-500">
        Aufwandsquote = Aufwendungen / Umsatz · JA-Anker
        {jaAnker
          ? ` aus Vorjahres-Dezember-BWA (${jaAnker.jahr})`
          : " noch nicht vorhanden"}
        . Quelle: BWA-Anhang Pahl-Diederichsen.
      </p>
    </section>
  );
}
